<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import ItpButton from '../button/ItpButton.vue';
import { gridMessages } from '../../messages';
import type { ItpDataGridColumn } from './types';
import { columnKey } from './gridCell';

const props = withDefaults(
  defineProps<{
    headerTitle?: string;
    countLabel?: string;
    showRowCount?: boolean;
    hasActiveQuery?: boolean;
    canExport?: boolean;
    showRefresh?: boolean;
    exporting?: boolean;
    loading?: boolean;
    reloadIcon?: string;
    exportIcon?: string;
    columns?: ItpDataGridColumn[];
    showColumnPicker?: boolean;
    columnPickerIcon?: string;
  }>(),
  {
    headerTitle: '',
    countLabel: '',
    showRowCount: true,
    hasActiveQuery: false,
    canExport: false,
    showRefresh: true,
    exporting: false,
    loading: false,
    reloadIcon: 'ooui:reload',
    exportIcon: 'mdi:download',
    columns: () => [],
    showColumnPicker: true,
    columnPickerIcon: 'mdi:view-column-outline',
  },
);

const emit = defineEmits<{
  clear: [];
  refresh: [];
  'export-csv': [];
  'export-xlsx': [];
  'toggle-column': [key: string, hidden: boolean];
}>();

const menuOpen = ref(false);
const columnsOpen = ref(false);

const exportAnchor = ref<HTMLElement | null>(null);
const columnsAnchor = ref<HTMLElement | null>(null);
const menuStyle = ref<Record<string, string>>({});
const openAnchor = ref<HTMLElement | null>(null);

const MENU_MAX_WIDTH_PX = 240;

function placeMenu(anchor: HTMLElement | null): void {
  if (!anchor) {
    return;
  }

  const rect = anchor.getBoundingClientRect();
  const style: Record<string, string> = { top: `${rect.bottom + 2}px` };

  if (rect.left + MENU_MAX_WIDTH_PX > window.innerWidth) {
    style.right = `${Math.max(0, window.innerWidth - rect.right)}px`;
  } else {
    style.left = `${rect.left}px`;
  }

  menuStyle.value = style;
}

function openMenu(which: 'export' | 'columns'): void {
  const opening = which === 'export' ? !menuOpen.value : !columnsOpen.value;

  menuOpen.value = which === 'export' && opening;
  columnsOpen.value = which === 'columns' && opening;
  openAnchor.value = opening
    ? which === 'export'
      ? exportAnchor.value
      : columnsAnchor.value
    : null;

  placeMenu(openAnchor.value);
}

function reposition(): void {
  placeMenu(openAnchor.value);
}

watch(
  () => menuOpen.value || columnsOpen.value,
  (open) => {
    if (open) {
      window.addEventListener('scroll', reposition, true);
      window.addEventListener('resize', reposition);
    } else {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    }
  },
);

const ADJACENT_SLOT_GAP_PX = 72;
const FILL_SLACK_PX = 8;
const toolbar = ref<HTMLElement | null>(null);
const showSlotDivider = ref(false);

interface Box {
  element: Element;
  rect: DOMRect;
}

function paintedChildren(element: Element): Box[] {
  const boxes: Box[] = [];
  for (const child of element.children) {
    const rect = child.getBoundingClientRect();
    if (rect.width && rect.height) {
      boxes.push({ element: child, rect });
    }
  }
  return boxes;
}

function contentLeftOf(box: Box): number {
  const children = paintedChildren(box.element);
  if (!children.length) {
    return box.rect.left;
  }
  const first = children[0];
  return first.rect.width >= box.rect.width - FILL_SLACK_PX
    ? contentLeftOf(first)
    : first.rect.left;
}

function measureSlotDivider(): void {
  const slot = toolbar.value?.querySelector('.itp-data-grid__slot');
  const anchor = toolbar.value?.querySelector('.itp-data-grid__clear');
  if (!slot || !anchor) {
    showSlotDivider.value = false;
    return;
  }
  const lefts = paintedChildren(slot).map(contentLeftOf);
  showSlotDivider.value =
    lefts.length > 0 &&
    Math.min(...lefts) - anchor.getBoundingClientRect().right <=
      ADJACENT_SLOT_GAP_PX;
}

let slotChanges: MutationObserver | null = null;
let slotResizes: ResizeObserver | null = null;

onMounted(() => {
  const root = toolbar.value;
  if (!root) {
    return;
  }
  measureSlotDivider();
  if (typeof MutationObserver !== 'undefined') {
    slotChanges = new MutationObserver(measureSlotDivider);
    slotChanges.observe(root, { childList: true, subtree: true });
  }
  if (typeof ResizeObserver !== 'undefined') {
    slotResizes = new ResizeObserver(measureSlotDivider);
    slotResizes.observe(root);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', reposition, true);
  window.removeEventListener('resize', reposition);
  slotChanges?.disconnect();
  slotChanges = null;
  slotResizes?.disconnect();
  slotResizes = null;
});

const pickableColumns = computed(() =>
  props.columns
    .filter((column) => column.hideable !== false)
    .map((column) => ({
      key: columnKey(column),
      label: column.name || column.field,
      visible: !column.hidden,
    })),
);

const visibleCount = computed(
  () => pickableColumns.value.filter((column) => column.visible).length,
);

function toggleColumn(key: string, visible: boolean): void {
  emit('toggle-column', key, visible);
}

function closeMenus(): void {
  menuOpen.value = false;
  columnsOpen.value = false;
}

function exportXlsx(): void {
  menuOpen.value = false;
  emit('export-xlsx');
}

function exportCsv(): void {
  menuOpen.value = false;
  emit('export-csv');
}
</script>

<template>
  <div ref="toolbar" class="itp-data-grid__toolbar">
    <strong v-if="headerTitle" class="itp-data-grid__title">{{
      headerTitle
    }}</strong>
    <span v-if="showRowCount" class="itp-data-grid__count">
      {{ countLabel }} {{ gridMessages.rows }}
    </span>

    <div class="itp-data-grid__actions" role="group">
      <ItpButton
        v-if="showRefresh"
        variant="light"
        size="sm"
        :icon="reloadIcon"
        :spinning="loading"
        :tooltip="gridMessages.refresh"
        @click="$emit('refresh')"
      />
      <div
        v-if="canExport"
        ref="exportAnchor"
        class="itp-data-grid__export"
        @keydown.esc.stop="closeMenus"
      >
        <ItpButton
          variant="light"
          size="sm"
          :icon="exportIcon"
          icon-append="mdi:chevron-down"
          :spinning="exporting"
          :tooltip="gridMessages.export"
          @click="openMenu('export')"
        />
        <template v-if="menuOpen">
          <div class="itp-data-grid__backdrop" @click="menuOpen = false" />
          <div class="itp-data-grid__menu" :style="menuStyle">
            <button
              type="button"
              class="itp-data-grid__menuitem"
              :title="gridMessages.exportXlsx"
              @click="exportXlsx"
            >
              .xlsx
            </button>
            <button
              type="button"
              class="itp-data-grid__menuitem"
              :title="gridMessages.exportCsv"
              @click="exportCsv"
            >
              .csv
            </button>
          </div>
        </template>
      </div>
      <div
        v-if="showColumnPicker && pickableColumns.length"
        ref="columnsAnchor"
        class="itp-data-grid__export"
        @keydown.esc.stop="closeMenus"
      >
        <ItpButton
          variant="light"
          size="sm"
          :icon="columnPickerIcon"
          icon-append="mdi:chevron-down"
          :tooltip="gridMessages.columns"
          @click="openMenu('columns')"
        />
        <template v-if="columnsOpen">
          <div class="itp-data-grid__backdrop" @click="columnsOpen = false" />
          <div
            class="itp-data-grid__menu itp-data-grid__columnmenu"
            :style="menuStyle"
          >
            <label
              v-for="column in pickableColumns"
              :key="column.key"
              class="itp-data-grid__menuitem itp-data-grid__columnitem"
            >
              <input
                type="checkbox"
                :checked="column.visible"
                :disabled="column.visible && visibleCount <= 1"
                @change="toggleColumn(column.key, column.visible)"
              />
              <span>{{ column.label }}</span>
            </label>
          </div>
        </template>
      </div>
    </div>

    <span class="itp-data-grid__divider" />

    <ItpButton
      class="itp-data-grid__clear"
      variant="light"
      size="sm"
      :text="gridMessages.clearAllFilters"
      :disabled="!hasActiveQuery"
      @click="$emit('clear')"
    />

    <span v-if="showSlotDivider" class="itp-data-grid__divider" />

    <span class="itp-data-grid__slot"><slot name="toolbar" /></span>
  </div>
</template>
